import { useMutation as useReactQueryMutation } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import type { ErrorResponse } from './errors';

export type EmptyMutationResponse = Response & { data?: void };

/**
 * Wrapper around useMutation which provides improved types
 * for update requests which have no return data and use the default error response.
 * Mainly changes the type order so that consumers only really need to pass
 * the request body type and don't need to worry about the rest of the types.
 *
 * @param {UseMutationOptions<RequestBody, ErrorResponseType, TVariables, TContext>} options - The mutation options.
 * @example
 * const { mutate } = useMutation<UpdatePlanRequest>({
 *   mutationFn: updateOrgBilling,
 * });
 */
export default function useMutation<
  RequestBody = void,
  ResponseBody = EmptyMutationResponse,
  ErrorResponseType = ErrorResponse,
  TContext = unknown,
>(options: UseMutationOptions<ResponseBody, ErrorResponseType, RequestBody, TContext>) {
  return useReactQueryMutation<ResponseBody, ErrorResponseType, RequestBody, TContext>(options);
}
